import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AppButton, Card, Field, ScreenHeader } from "@/components/AppUI";
import { formatError, papi } from "@/lib/pizzeria";

const Section = ({ title, children, testId }) => (
  <Card className="p-4 space-y-3" testId={testId}><p className="text-base font-medium">{title}</p>{children}</Card>
);

const useSubmit = () => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      return true;
    } catch (e) {
      toast.error(formatError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
};

const ProfileForm = ({ user, setUser }) => {
  const [f, setF] = useState({ name: user.name || "", phone: user.phone || "" });
  const [busy, run] = useSubmit();
  const save = () => {
    if (!f.name.trim()) return toast.error("Inserisci il tuo nome");
    run(async () => {
      const { data } = await papi.patch("/auth/me", { name: f.name.trim(), phone: f.phone.trim() });
      setUser(data.user || data);
    }, "Profilo aggiornato");
  };
  return (
    <Section title="I tuoi dati" testId="account-profile-section">
      <Field label="Nome" testId="account-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <Field label="Telefono" testId="account-phone-input" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      <AppButton title="Salva" loading={busy} onClick={save} testId="account-save-button" className="w-full" />
    </Section>
  );
};

const PasswordForm = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, run] = useSubmit();
  const save = async () => {
    if (!f.current_password) return toast.error("Inserisci la password attuale");
    if (f.new_password.length < 6) return toast.error("La nuova password deve avere almeno 6 caratteri");
    if (await run(() => papi.post("/auth/change-password", f), "Password aggiornata")) setF({ current_password: "", new_password: "" });
  };
  return (
    <Section title="Cambia password" testId="account-password-section">
      <Field label="Password attuale" testId="account-current-password-input" type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} />
      <Field label="Nuova password" testId="account-new-password-input" type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} />
      <AppButton variant="dark" title="Aggiorna password" loading={busy} onClick={save} testId="account-password-button" className="w-full" />
    </Section>
  );
};

const DeleteForm = ({ logout }) => {
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, run] = useSubmit();
  const del = async () => {
    if (!pw) return toast.error("Inserisci la password per confermare");
    if (!window.confirm("Vuoi eliminare definitivamente il tuo account? Perderai ordini salvati e carta fedeltà.")) return;
    if (await run(() => papi.delete("/auth/me", { data: { current_password: pw } }), "Account eliminato")) {
      logout();
      nav("/profilo", { replace: true });
    }
  };
  return (
    <Section title="Elimina account" testId="account-delete-section">
      <p className="text-sm" style={{ color: "var(--muted-c)" }}>Elimina per sempre il tuo account e i tuoi dati personali.</p>
      <Field label="Password" testId="account-delete-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
      <AppButton variant="outline" title="Elimina il mio account" loading={busy} onClick={del} testId="account-delete-button" className="w-full !text-[var(--brand)]" />
    </Section>
  );
};

export default function AccountPage() {
  const { user, setUser, logout } = useAuth();
  if (user === false) return <Navigate to="/profilo" replace />;
  if (!user) return null;
  return (
    <div className="flex-1" data-testid="account-page">
      <ScreenHeader title="Il mio account" fallback="/profilo" />
      <div className="p-4 pb-8 space-y-4">
        <ProfileForm user={user} setUser={setUser} />
        <PasswordForm />
        <DeleteForm logout={logout} />
      </div>
    </div>
  );
}
